import React, { useState, useEffect } from "react";
import { PermissionType } from "../../../Enums";
import { Button } from "@mui/material";
import FormEditor from "./FormEditor/FormEditor";
import AdminLevelControls from "./AdminLevelControls";
import SuperAdminControls from "./SuperAdminControls";
import SyncIcon from "@mui/icons-material/Sync";
import LogoutIcon from "@mui/icons-material/Logout";
import FaceIcon from "@mui/icons-material/Face";
import EditNoteIcon from "@mui/icons-material/EditNote";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { DataStore } from "@aws-amplify/datastore";
import { signOut } from "@aws-amplify/auth";
import "./Admin.css";

const Admin = (props) => {
  const {
    viewfieldsInstance,
    appUser,
    customTheme,
    dictionaryResolvers,
    formDictionaries,
    formPages,
    formLanguages,
    formName,
    formToken,
    onSaveForm,
    onSaveAssets,
    onProjectsChange,
    isAdminOpen,

    allSubmissions,
    currentProject,
    currentForm,
    assets,
    fields,
    projectHistoryLogs,
    currentProjectName,
    currentProjectToken,
    submissionToken,
    reviewingTokens,
    activeReviewToken,
    setActiveReviewToken,
    adminUsers,
    adminTokens,
    onCreateUser,
    onCellUpdate,
    treeData,
    onNewProject
  } = props;

  const [expanded, setExpanded] = useState(false);

  // "ADMIN_SETTINGS" | "FORM_EDITOR"
  const [view, setView] = useState("ADMIN_SETTINGS");

  useEffect(() => {
    setExpanded(!!isAdminOpen);
  }, [isAdminOpen]);

  const isSuperAdmin = appUser?.adminLevel === PermissionType.SUPER_ADMIN;

  // --- Handlers (unchanged) ---
  const handleRefresh = async () => {
    try {
      await DataStore.clear();
      await DataStore.stop();
      Object.keys(localStorage).forEach((key) => {
        if (key !== "LastAuthUser" && !key.includes("CognitoIdentityServiceProvider")) {
          localStorage.removeItem(key);
        }
      });
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.error("Error clearing the datastore:", error);
      window.location.reload();
    }
  };

  const handleLogout = async () => {
    try {
      await DataStore.clear();
      localStorage.clear();
      await DataStore.stop();
    } catch (error) {
      console.error("Error clearing the datastore:", error);
    }
    try {
      await signOut();
      window.location.href = "/";
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleMe = () => {
    console.log("Me button clicked...");
  };

  // --- View switching ---
  const handleShowAdminSettings = () => setView("ADMIN_SETTINGS");
  const handleShowFormEditor = () => setView("FORM_EDITOR");

  // Derived flags for selected state
  const isAdminSelected = view === "ADMIN_SETTINGS";
  const isFormEditorSelected = view === "FORM_EDITOR";

  // Root classes (unchanged)
  const rootClass = [
    isSuperAdmin ? "role-superadmin" : "role-admin",
    isFormEditorSelected ? "mode-form-editor" : "mode-admin-settings",
  ]
    .filter(Boolean)
    .join(" ");

  const adminLevelProps = {
    // required / used by AdminLevelControls
    projectHistoryLogs,
    currentProjectName,
    currentProjectToken,
    submissionToken,
    reviewingTokens,
    activeReviewToken,
    setActiveReviewToken,
    appUser,
    adminTokens,
    isAdminOpen,
    onProjectsChange,
    customTheme,

    // super-admin features (used conditionally inside AdminLevelControls)
    allSubmissions,
    currentProject,
    currentForm,
    assets,
    processedFields: fields,
    treeData,
    adminUsers,
    onNewProject,
    onCellUpdate,
    onCreateUser,

    // included for completeness / future use
    viewfieldsInstance,
  };

  // --- Renderers (unchanged) ---
  const renderAdminContent = () => {
    return (
      <div className="admin-layout">
        <div className="admin-section">
          <AdminLevelControls {...adminLevelProps} />
        </div>
      </div>
    );
  };

  const renderFormEditorContent = () => (
    <div className="form-editor-container">
      <FormEditor
        viewfieldsInstance={viewfieldsInstance}
        dictionaryResolvers={dictionaryResolvers}
        formDictionaries={formDictionaries}
        formPages={formPages}
        formLanguages={formLanguages}
        formName={formName}
        formToken={formToken}
        languages={["en", "fr"]} // or derive from your dictionaries
        initialLanguage="en"
        superAdmin={isSuperAdmin}
        onSaveForm={(partialUpdate) => {
          console.log("Admin: FormEditor onSaveForm called:", partialUpdate);
          onSaveForm?.(partialUpdate);
        }}
        onSaveAssets={(updatedAssetsMap) => {
          console.log("Admin: FormEditor onSaveAssets called:", updatedAssetsMap);
          onSaveAssets?.(updatedAssetsMap);
        }}
      />
    </div>
  );

  return (
    <div className="admin-root">
      {/* Buttons row (second row of icons) */}
      <div className="buttons-container" role="toolbar" aria-label="Admin toolbar">
        <Button
          title="Admin"
          onClick={handleShowAdminSettings}
          className={`admin-icon ${isAdminSelected ? "is-selected" : ""}`}
          aria-pressed={isAdminSelected}
          aria-current={isAdminSelected ? "page" : undefined}
        >
          <AdminPanelSettingsIcon />
        </Button>

        <Button
          title="Form Editor"
          onClick={handleShowFormEditor}
          className={`admin-icon ${isFormEditorSelected ? "is-selected" : ""}`}
          aria-pressed={isFormEditorSelected}
          aria-current={isFormEditorSelected ? "page" : undefined}
        >
          <EditNoteIcon />
        </Button>

        <Button title="Refresh Data" onClick={handleRefresh} className="admin-icon">
          <SyncIcon />
        </Button>
        <Button title="Logout" onClick={handleLogout} className="admin-icon">
          <LogoutIcon />
        </Button>
        <Button title="Me" onClick={handleMe} className="admin-icon">
          <FaceIcon />
        </Button>
      </div>

      <div className={rootClass}>
        {/* Content area: switch between Admin and Form Editor */}
        <div className="admin-content-area">
          {isFormEditorSelected ? renderFormEditorContent() : renderAdminContent()}
        </div>
      </div>
    </div>
  );
};

export default Admin;
